import './chatInput.scss';
import {Icons} from '../../icons/icons';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useStateConversation, useStateShowChatSuggestions} from '../duck/assistant-hooks';
import {useTranslation} from 'react-i18next';
import {useWithConfirmation} from '../../common/hibuModal/hibuModal';
import {
    useAiAssistantHelpModalContentEnglish,
    useAiAssistantHelpModalContentGerman,
    useAiAssistantSupportedTags,
    useAiAssistantWebsearchEnabled,
} from '../../config/config-hooks';
import {getCurrentLanguage} from '../../i18n-service';
import {
    AiAssistantConversationContext,
    CapabilityOption,
    DEFAULT_ASSISTANT_CONVERSATION_CONTEXT,
    HibuTagDescription
} from './types';
import {ChatInputSettings} from './chatInputSettings';
import {ChatInputCommands} from './chatInputCommands';
import {updateConversationContextAction} from '../duck/assistant-actions';
import {useDispatch} from 'react-redux';
import {TFunction} from 'i18next';
import {HibuButton} from '../../common/hibuButton/hibuButton';

const CAPABILITIES = {
    DOCUMENTS: 'documents',
    WEB: 'web',
} as const;

type AssistantCapabilities = (typeof CAPABILITIES)[keyof typeof CAPABILITIES];
const CAPABILITY_ORDER: AssistantCapabilities[] = [CAPABILITIES.DOCUMENTS, CAPABILITIES.WEB];
const COMMAND_MENU_PATTERN = /(?:^|\s)@(\S*)$/;

const stripCommandPrefix = (label: string): string => label.replace(/^@\s*/, '');

export const getCommandMenuMatch = (value: string): { filter: string; startIndex: number } | null => {
    const match = COMMAND_MENU_PATTERN.exec(value);
    if (match?.index === undefined) {
        return null;
    }
    return {filter: match[1], startIndex: match.index + match[0].lastIndexOf('@')};
};

export const extractBodyFromInput = (message: string): string => {
    return message.replace(/^(?:@\S*\s*)+/, '').trim();
};

export const extractCapabilitiesFromInput = (
    input: string,
    capabilities: Set<string>,
    t: TFunction<'translation', undefined>,
): string[] => {
    const words = input.trim().split(/\s+/);
    const result: string[] = [];

    for (const word of words) {
        if (!word.startsWith('@')) {
            break;
        }
        const capability = Array.from(capabilities).find(
            (capability) => t(`assistant.capabilities.${capability}.label`) === word,
        );
        if (capability) {
            result.push(capability);
        }
    }
    return result;
};

export const constructInputWithCapabilityPrefixes = (
    input: string,
    selectedCapability: string,
    capabilitiesFromInput: string[],
    configCapabilities: string[],
    t: TFunction<'translation', undefined>,
): string => {
    const leadingWhitespace = new RegExp(/^\s*/).exec(input)?.[0] ?? '';
    const body: string = extractBodyFromInput(input);
    const enabledCapabilities: string[] = toggleInArray(capabilitiesFromInput, selectedCapability);
    const capabilityPrefixes: string = getSortedCapabilities(enabledCapabilities, configCapabilities)
        .map((capability) => t(`assistant.capabilities.${capability}.label`))
        .join(' ');

    if (!capabilityPrefixes) {
        return `${leadingWhitespace}${body}`.trimEnd();
    }
    if (!body) {
        return `${leadingWhitespace}${capabilityPrefixes} `;
    }
    return `${leadingWhitespace}${capabilityPrefixes} ${body}`;
};

const getSortedCapabilities = (enabledCapabilities: string[], capabilitiesFromConfig: string[]): string[] => [
    ...CAPABILITY_ORDER.filter((capability) => enabledCapabilities.includes(capability)),
    ...capabilitiesFromConfig.filter((capability) => enabledCapabilities.includes(capability)),
];

const toggleInArray = (array: string[], value: string): string[] => {
    if (array.includes(value)) {
        return array.filter((item) => item !== value);
    }
    return [...array, value];
};

export type ChatInputProps = {
    conversationId: string;
    getAiResponse: (inputParameter: string, contextOverride?: AiAssistantConversationContext) => Promise<void>;
    getAiSuggestions?: () => void;
    welcomeMessage?: string;
    handleResetConversation: () => void;
};

export const ChatInput = ({
                              conversationId,
                              getAiResponse,
                              welcomeMessage,
                              getAiSuggestions,
                              handleResetConversation,
                          }: ChatInputProps): React.JSX.Element => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const currentLanguage = getCurrentLanguage();

    const [input, setInput] = useState('');
    const [isCommandMenuOpen, setIsCommandMenuOpen] = useState(false);
    const [isCommandMenuFocused, setIsCommandMenuFocused] = useState(false);
    const [modalText, setModalText] = useState<string>('');
    const inputRef = useRef<HTMLTextAreaElement>(null);

    const showSuggestions: boolean = useStateShowChatSuggestions();
    const websearchEnabled: boolean = useAiAssistantWebsearchEnabled();
    const supportedTags: HibuTagDescription[] = useAiAssistantSupportedTags();
    const helpModalContentGerman: string = useAiAssistantHelpModalContentGerman();
    const helpModalContentEnglish: string = useAiAssistantHelpModalContentEnglish();
    const conversationContext: AiAssistantConversationContext = useStateConversation(conversationId)?.context ?? DEFAULT_ASSISTANT_CONVERSATION_CONTEXT;

    const hasCommandOptions: boolean = websearchEnabled || supportedTags.length > 0;

    const activeCapabilities: string[] = useMemo(
        () => [
            ...(conversationContext.enableDocuments ? [CAPABILITIES.DOCUMENTS] : []),
            ...(conversationContext.enableWebSearch ? [CAPABILITIES.WEB] : []),
            ...conversationContext.tags,
        ],
        [conversationContext.enableDocuments, conversationContext.enableWebSearch, conversationContext.tags],
    );

    const capabilitiesFromSupportedTags: string[] = useMemo(() => supportedTags.map((tag: HibuTagDescription) => tag.name), [supportedTags]);

    const capabilityOptions: CapabilityOption[] = [
        ...[CAPABILITIES.DOCUMENTS, ...(websearchEnabled ? [CAPABILITIES.WEB] : [])].map((name) => ({
            type: name,
            label: t(`assistant.capabilities.${name}.label`),
            commandLabel: stripCommandPrefix(t(`assistant.capabilities.${name}.label`)),
        })),
        ...Array.from(capabilitiesFromSupportedTags).map(tagName => ({
            type: tagName,
            label: '@' + t(`fieldTitle.tagsPlural.${tagName}.label`),
            commandLabel: t(`fieldTitle.tagsPlural.${tagName}.label`),
        }))
    ];

    const capabilitiesFromAllSources = new Set<string>(capabilityOptions.map((option: CapabilityOption) => option.type));

    useEffect(() => {
        switch (currentLanguage) {
            case 'en':
                setModalText(helpModalContentEnglish);
                break;
            case 'de':
                setModalText(helpModalContentGerman);
                break;
            default:
                setModalText(helpModalContentEnglish);
        }
    }, [helpModalContentEnglish, helpModalContentGerman, currentLanguage]);

    useEffect(() => {
        setInput(hasCommandOptions ?
            getSortedCapabilities(activeCapabilities, capabilitiesFromSupportedTags)
                .map((capability) => t(`assistant.capabilities.${capability}.label`))
                .join(' ') + ' ' :
            '');
    }, [capabilitiesFromSupportedTags, activeCapabilities, t, hasCommandOptions]);

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    const updateContext = (
        context: AiAssistantConversationContext,
        capabilities: string[],
    ): AiAssistantConversationContext => {
        const updatedContext = {
            ...context,
            enableDocuments: capabilities.includes(CAPABILITIES.DOCUMENTS),
            enableWebSearch: capabilities.includes(CAPABILITIES.WEB),
            tags: capabilities.filter(
                (capability) =>
                    capability !== CAPABILITIES.DOCUMENTS && capability !== CAPABILITIES.WEB,
            ),
        };
        dispatch(updateConversationContextAction(conversationId, updatedContext));
        return updatedContext;
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = event.target.value;
        setInput(value);
        const match = getCommandMenuMatch(value);
        if (match) {
            setIsCommandMenuOpen(true);
            return;
        }
        setIsCommandMenuOpen(false);
    };

    const handleKeyPress = async (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        inputRef.current?.focus();
        setIsCommandMenuFocused(false);
        if (isCommandMenuOpen) {
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                setIsCommandMenuFocused(true);
                return;
            }
            if (event.key === 'ArrowUp') {
                event.preventDefault();
                setIsCommandMenuFocused(true);
                return;
            }
        }
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            await handleSendMessage();
        }
    };

    const handleToggleCommandMenu = (nextShow: boolean) => {
        setIsCommandMenuOpen(nextShow);
        if (!nextShow) {
            inputRef.current?.focus();
        }
    };

    const handleSelectCommand = (selectedCapability: string | null) => {
        if (selectedCapability) {
            const capabilitiesFromInput: string[] = extractCapabilitiesFromInput(input, capabilitiesFromAllSources, t);
            const updatedInput: string = constructInputWithCapabilityPrefixes(
                input,
                selectedCapability,
                capabilitiesFromInput,
                capabilitiesFromSupportedTags,
                t,
            );

            setInput(updatedInput);
            setIsCommandMenuOpen(false);
            inputRef.current?.focus();
        }
    };

    const handleSendMessage = async () => {
        const capabilitiesFromInput: string[] = hasCommandOptions ? extractCapabilitiesFromInput(input, capabilitiesFromAllSources, t) : [CAPABILITIES.DOCUMENTS];
        const updatedContext = updateContext(conversationContext, capabilitiesFromInput);

        const msg = extractBodyFromInput(input);

        const capabilityPrefixes: string = hasCommandOptions ? capabilitiesFromInput
            .map((capability) => t(`assistant.capabilities.${capability}.label`))
            .join(' ') : '';
        setInput(capabilityPrefixes + ' ');
        setIsCommandMenuOpen(false);

        try {
            await getAiResponse(msg, updatedContext);
            if (getAiSuggestions && showSuggestions) {
                getAiSuggestions();
            }
        } catch (err) {
            console.error('Error:', err);
        }
    };

    const {onClick: handleOpenHelp} = useWithConfirmation(modalText, {
        backdrop: true,
        keyboard: true,
        closeButton: false,
        size: 'lg' as const,
        title: t('search.details.hibu-assistant.help.popup.title'),
        ok: t('general.close'),
        dialogClassName: 'assistant-help-modal',
        useMarkdown: true,
    });

    return (
        <div className="chat-input">
            <div className="assistant-input-wrapper">
                <div className="assistant-input-textarea-wrapper">
                    <textarea
                        disabled={false}
                        readOnly={false}
                        ref={inputRef}
                        value={input}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyPress}
                        placeholder={welcomeMessage}
                        className="assistant-input-field"
                    />
                    <div className="assistant-input-toolbar">
                        <ChatInputSettings handleReset={handleResetConversation} handleHelp={handleOpenHelp()}/>
                        {
                            hasCommandOptions &&
                            <ChatInputCommands
                                options={capabilityOptions}
                                onSelectCommand={handleSelectCommand}
                                isOpen={isCommandMenuOpen}
                                setFocus={isCommandMenuFocused}
                                onToggle={handleToggleCommandMenu}
                            ></ChatInputCommands>
                        }
                    </div>
                </div>
                <div className="assistant-input-buttons-wrapper">
                    <HibuButton
                        visible={true}
                        icon={Icons.SEND}
                        onClick={handleSendMessage}
                        altText={t('general.send')}
                    />
                </div>
            </div>
            <div className="assistant-input-disclaimer">{t('assistant.input-disclaimer')}</div>
        </div>
    );
};
